import * as Sentry from '@sentry/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useToastMessageState } from '../../../../components/ToastMessage/ToastMessageProvider';
import { queryClient } from '../../../../services/MaggieQueryClient';
import {
  bulkUpdateKpiCollectionCompanySettings,
  createKpiCollectionConfiguration,
  deleteKpiCollectionConfiguration,
  fetchKpiCollectionCompanySettings,
  fetchKpiCollectionConfigurations,
  updateKpiCollectionConfiguration,
} from '../../../../services/queries/KpiCollectionQueries';
import { getErrorMessage } from '../../../../services/queryHelpers';
import {
  KpiCollectionConfiguration,
  KpiCollectionConfigurationResponse,
} from '../../../../schemas/KpiCollectionConfiguration.schema';
import {
  BulkUpsertKpiCompanySettingsResponse,
  KpiCollectionCompanySettingsPayload,
} from '../../../../schemas/KpiCollectionCompanySettings.schema';

export const KpiCollectionSettingsQueryKey = ['kpi-collection-settings'] as const;
export const KpiCollectionCompanySettingsQueryKey = ['kpi-collection-company-settings'] as const;

export const useKpiCollectionSettingsState = () => {
  return useQuery({
    queryKey: KpiCollectionSettingsQueryKey,
    queryFn: fetchKpiCollectionConfigurations,
  });
};

export function useCreateKpiCollectionConfiguration() {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  return useMutation({
    mutationFn: (configuration: KpiCollectionConfiguration) =>
      createKpiCollectionConfiguration(configuration),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KpiCollectionSettingsQueryKey });
      pushSuccessToast({ message: 'KPI collection configuration created successfully' });
    },
    onError: (error) => {
      console.error(error);
      Sentry.logger.error(error.message);
      pushErrorToast({
        message: getErrorMessage(error, 'Failed to create KPI collection configuration'),
      });
    },
  });
}

export function useUpdateKpiCollectionConfiguration() {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  return useMutation({
    mutationFn: ({
      id,
      configuration,
    }: {
      id: KpiCollectionConfigurationResponse['id'];
      configuration: KpiCollectionConfiguration;
    }) => updateKpiCollectionConfiguration(id, configuration),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KpiCollectionSettingsQueryKey });
      pushSuccessToast({ message: 'KPI collection configuration updated successfully' });
    },
    onError: (error) => {
      console.error(error);
      Sentry.logger.error(error.message);
      pushErrorToast({
        message: getErrorMessage(error, 'Failed to update KPI collection configuration'),
      });
    },
  });
}

export function useDeleteKpiCollectionConfiguration() {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  return useMutation({
    mutationFn: (id: KpiCollectionConfigurationResponse['id']) => deleteKpiCollectionConfiguration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KpiCollectionSettingsQueryKey });
      pushSuccessToast({ message: 'KPI collection configuration deleted successfully' });
    },
    onError: (error) => {
      console.error(error);
      Sentry.logger.error(error.message);
      pushErrorToast({
        message: getErrorMessage(error, 'Failed to delete KPI collection configuration'),
      });
    },
  });
}

export function useKpiCollectionCompanySettingsState() {
  return useQuery({
    queryKey: KpiCollectionCompanySettingsQueryKey,
    queryFn: fetchKpiCollectionCompanySettings,
    refetchOnWindowFocus: false,
  });
}

interface BulkUpdateOptions {
  invalidateOnSuccess?: boolean;
  showSuccessToast?: boolean;
}

// should be just one endpoint for create/update
export function useBulkUpdateKpiCollectionCompanySettings(options: BulkUpdateOptions = {}) {
  const { invalidateOnSuccess = true, showSuccessToast = true } = options;
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  return useMutation({
    mutationFn: (settings: KpiCollectionCompanySettingsPayload[]) =>
      bulkUpdateKpiCollectionCompanySettings(settings),
    onSuccess: (result: BulkUpsertKpiCompanySettingsResponse) => {
      if (invalidateOnSuccess) {
        queryClient.invalidateQueries({ queryKey: KpiCollectionCompanySettingsQueryKey });
      }

      if (result?.errors?.length) {
        const errorMessages = result.errors.map((e) => e.message).join('\n');
        pushErrorToast({ message: errorMessages });
      } else if (showSuccessToast) {
        pushSuccessToast({ message: 'KPI collection company settings updated successfully' });
      }
    },
    onError: (error) => {
      console.error(error);
      Sentry.logger.error(error.message);
      pushErrorToast({
        message: getErrorMessage(error, 'Failed to update KPI collection company settings'),
      });
    },
  });
}
